import { useEffect } from "react";

/**
 * Custom hook untuk menangani event Enter pada suatu input:
 * 1. Klik tombol "Add Row" (SimpleFormIterator)
 * 2. Fokus ke input "code" di baris baru
 *
 * @param {React.RefObject<HTMLElement>} targetRef Ref ke container input (misalnya <Grid item ref={targetRef}>)
 * @param {string} addButtonSelector Selector CSS untuk tombol "Add" (default: SimpleFormIterator's add button)
 * @param {string} newRowInputSelector Selector CSS untuk input yang dituju di baris baru
 */
const useEnterToAddRowAndFocus = (
  targetRef,
  addButtonSelector = ".RaSimpleFormIterator-add button, .button-add-purchase_order_items",
  newRowInputSelector = 'input[name*="code"]'
) => {
  useEffect(() => {
    // Cari elemen <input> di dalam ref container
    const inputEl = targetRef.current?.querySelector("input");
    if (!inputEl) return;
    let mounted = true;

    const handleEnter = (e) => {
      if (e.key !== "Enter") return;
      e.preventDefault();

      // Hitung jumlah baris sebelum tambah
      const rowsBefore = document.querySelectorAll(
        ".RaSimpleFormIterator-line"
      ).length;

      // Cari tombol tambah dan klik
      const addButton = document.querySelector(addButtonSelector);
      if (!addButton) return;
      addButton.click();

      // 🔹 Polling untuk menunggu row baru muncul
      const start = Date.now();
      const timeout = 2000;
      const interval = 30;

      const waitForNewRow = () => {
        if (!mounted) return;
        const rowsNow = document.querySelectorAll(
          ".RaSimpleFormIterator-line"
        ).length;

        if (rowsNow > rowsBefore) {
          const rows = document.querySelectorAll(".RaSimpleFormIterator-line");
          const lastRow = rows[rows.length - 1];
          if (lastRow) {
            // Cari input target di baris terakhir
            const targetInput =
              lastRow.querySelector(newRowInputSelector) ||
              lastRow.querySelector("input"); // Fallback ke input pertama
            if (targetInput) {
              // Fokus
              setTimeout(() => targetInput.focus(), 10);
            }
          }
          return; // Stop polling
        }

        if (Date.now() - start > timeout) return; // Timeout
        setTimeout(waitForNewRow, interval);
      };

      setTimeout(waitForNewRow, interval);
    };

    // Tambahkan event listener
    inputEl.addEventListener("keydown", handleEnter);

    // Cleanup function
    return () => {
      mounted = false;
      inputEl.removeEventListener("keydown", handleEnter);
    };
  }, [targetRef, addButtonSelector, newRowInputSelector]);
};

export default useEnterToAddRowAndFocus;